import React, { useEffect, useState } from 'react';
import { ListChecks, Loader2, CheckSquare, Square, Upload, Paperclip, CheckCircle2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';

interface OnboardingStep { id: string; label: string; type: 'checkbox' | 'upload'; description: string; required: boolean; }
interface Assignment {
    id: string;
    template_name: string;
    steps_snapshot: OnboardingStep[];
    progress: Record<string, { completed: boolean }>;
    status: 'in_progress' | 'completed';
    has_attachment: boolean;
}

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = reader.result as string;
        const parts = result.split(';base64,');
        resolve(parts.length > 1 ? parts[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export const EmployeeOnboarding: React.FC = () => {
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [loading, setLoading] = useState(true);
    const [busyStep, setBusyStep] = useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const pendingUploadRef = React.useRef<{ assignmentId: string; stepId: string } | null>(null);

    const load = () => {
        moduleApi.getMyOnboarding().then((d: any) => setAssignments(Array.isArray(d) ? d : [])).catch(() => setAssignments([])).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    // window.open() doit s'exécuter de façon SYNCHRONE au clic (avant tout await) sous peine d'être
    // bloqué silencieusement par le navigateur (geste utilisateur perdu après le fetch).
    const viewAttachment = async (assignmentId: string, win: Window | null) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/my-onboarding/${assignmentId}/attachment`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { win?.close(); return; }
            const blob = await res.blob();
            if (win) win.location.href = URL.createObjectURL(blob);
        } catch { win?.close(); }
    };

    const toggleCheck = async (assignmentId: string, step: OnboardingStep, currentlyDone: boolean) => {
        setBusyStep(step.id);
        try {
            await moduleApi.checkOnboardingStep(assignmentId, step.id, !currentlyDone);
            await load();
        } finally {
            setBusyStep(null);
        }
    };

    const triggerUpload = (assignmentId: string, stepId: string) => {
        pendingUploadRef.current = { assignmentId, stepId };
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        const target = pendingUploadRef.current;
        if (!file || !target) return;
        setBusyStep(target.stepId);
        try {
            const base64 = await fileToBase64(file);
            await moduleApi.uploadOnboardingStep(target.assignmentId, target.stepId, {
                display_name: file.name, file_name: file.name, mime_type: file.type || 'application/octet-stream', base64,
            });
            await load();
        } finally {
            setBusyStep(null);
        }
    };

    return (
        <div className="space-y-6">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><ListChecks size={24} /> Mon intégration</h1>
                <p className="text-content-muted mt-1">Les parcours d'intégration assignés par votre établissement.</p>
            </header>

            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelected} />

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : assignments.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <ListChecks size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucun parcours pour l'instant.</p>
                </div>
            ) : (
                <div className="max-w-2xl space-y-4">
                    {assignments.map((a) => {
                        const total = a.steps_snapshot.length;
                        const done = a.steps_snapshot.filter((s) => a.progress?.[s.id]?.completed).length;
                        return (
                            <div key={a.id} className="p-5 rounded-2xl bg-surface-soft border border-border space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <p className="font-bold text-content text-sm">{a.template_name}</p>
                                    <span className={`shrink-0 text-[10px] font-black uppercase px-2 py-1 rounded-lg border ${a.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-warning-500/10 text-warning-500 border-warning-500/20'}`}>
                                        {a.status === 'completed' ? 'Terminé' : `${done}/${total} étapes`}
                                    </span>
                                </div>

                                {a.has_attachment && (
                                    <button onClick={() => viewAttachment(a.id, window.open('', '_blank'))}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                        <Paperclip size={11} /> Voir le document
                                    </button>
                                )}

                                <div className="space-y-2">
                                    {a.steps_snapshot.map((s) => {
                                        const isDone = !!a.progress?.[s.id]?.completed;
                                        const isBusy = busyStep === s.id;
                                        return (
                                            <div key={s.id} className="flex items-start gap-3 p-3 rounded-xl bg-surface border border-border">
                                                {s.type === 'checkbox' ? (
                                                    <button onClick={() => toggleCheck(a.id, s, isDone)} disabled={isBusy} className="mt-0.5 shrink-0 text-content-muted hover:text-brand-500 disabled:opacity-50 transition-colors">
                                                        {isBusy ? <Loader2 size={18} className="animate-spin" /> : isDone ? <CheckSquare size={18} className="text-emerald-500" /> : <Square size={18} />}
                                                    </button>
                                                ) : (
                                                    <div className="mt-0.5 shrink-0">{isDone ? <CheckCircle2 size={18} className="text-emerald-500" /> : <Upload size={18} className="text-content-muted" />}</div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-bold ${isDone ? 'text-content-muted line-through' : 'text-content'}`}>{s.label}{s.required && !isDone && <span className="text-red-400 ml-1">*</span>}</p>
                                                    {s.description && <p className="text-xs text-content-muted mt-0.5">{s.description}</p>}
                                                    {s.type === 'upload' && !isDone && (
                                                        <button onClick={() => triggerUpload(a.id, s.id)} disabled={isBusy} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50 transition-all">
                                                            {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Fournir le document
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default EmployeeOnboarding;
